import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Box,
  Checkbox,
  IconButton,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  useTheme
} from "@mui/material";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store/store";
import NoStoreListFound from "./NoStoreListFound";
import AddStoreModal from "./AddStoreModal";
import AppButton from "../common/AppButton";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  PAGE_PORTAL_STORES,
  PORTAL_PREFIX,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import CommonLoader from "../common/CommonLoader/CommonLoader";
import { CompanyStore } from "./types/storeType";
import { useGetCompanyStores } from "../../hooks/api/useStore";
import ExportData from "../common/ExportData/ExportData";
import { exportStoreColumns } from "../common/constants/exportColumns";

import { buildLogContext, captureAppException } from "../../Utils/appLogger";

type Order = "asc" | "desc";

const StoreList: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useKivunovaTranslation();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};
  const storeList = useSelector((state: RootState) => state.store.storeList);
  const userDetails = useSelector((state: RootState) => state.user.userDetails);
  const [openAddModal, setOpenAddModal] = useState(false);
  const useGetCompanyStoresMutation = useGetCompanyStores();
  const theme = useTheme();
  const matchMD = useMediaQuery(theme.breakpoints.down("md"));

  const [search, setSearch] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<keyof CompanyStore>("name");

  useEffect(() => {
    (async () => {
      try {
        if (userDetails?.companyId) {
          await useGetCompanyStoresMutation.mutateAsync({
            companyId: userDetails.companyId
          });
        }
      } catch (e) {
        captureAppException(
          "StoreList: Failed to fetch store list",
          e,
          buildLogContext(
            "stores",
            "get-company-stores",
            userDetails?.id,
            userDetails?.companyId,
            userDetails?.storeId
          )
        );
      }
    })();
  }, [userDetails?.companyId]);

  /** ✅ Filter by search */
  const filteredList = useMemo(() => {
    if (!search.trim()) return storeList;
    return storeList.filter((store) =>
      Object.values(store)
        .join(" ")
        .toLowerCase()
        .includes(search.toLowerCase())
    );
  }, [storeList, search]);

  /** ✅ Sorting logic */
  const sortedList = useMemo(() => {
    return [...filteredList].sort((a, b) => {
      const aValue = a[orderBy] ?? "";
      const bValue = b[orderBy] ?? "";
      if (typeof aValue === "number" && typeof bValue === "number") {
        return order === "asc" ? aValue - bValue : bValue - aValue;
      }
      return order === "asc"
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
  }, [filteredList, order, orderBy]);

  /** ✅ Handle selection */
  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedIds(sortedList.map((p) => p.id));
    } else {
      setSelectedIds([]);
    }
  };

  const handleSelectOne = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((pid) => pid !== id) : [...prev, id]
    );
  };

  /** ✅ Handle sort toggle */
  const handleSort = (property: keyof CompanyStore) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const goToStoreDetail = (storeId: string) => {
    navigate(
      localizedPath(
        `/${PORTAL_PREFIX}/${PAGE_PORTAL_STORES}/${storeId}`,
        language,
        defaultLang
      )
    );
  };

  /** ✅ Get data to export (either selected or all) */
  const getExportData = () => {
    if (selectedIds.length > 0) {
      return sortedList.filter((store) => selectedIds.includes(store.id));
    }
    return sortedList;
  };

  if (useGetCompanyStoresMutation.isPending) {
    return (
      <CommonLoader
        loadingMessage={t("store-fetching-list", "Fetching store list...")}
      />
    );
  }

  if (!storeList.length) {
    return <NoStoreListFound />;
  }

  return (
    <Box
      data-testid="company-store-list"
      sx={{
        minHeight: "calc(100vh - 110px)",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        width: "100%",
        maxWidth: "100%",
        padding: 0,
        pt: 2,
        color: "black",
        mt: 1
      }}
    >
      <Box gap={4} flexDirection="column" display="flex">
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          gap={2}
          flexDirection={matchMD ? "column" : "row"}
        >
          <Typography
            variant="h5"
            fontWeight="bold"
            textAlign="center"
            color="textPrimary"
          >
            {t("store-list-title", "Store List")}
          </Typography>

          <Box
            display="flex"
            alignItems="center"
            gap={2}
            flexDirection={matchMD ? "column" : "row"}
          >
            <Box display="flex" flexDirection="row" gap={2}>
              <AppButton
                variant="primary"
                onClick={() => setOpenAddModal(true)}
              >
                {t("store-add-button", "Add Store")}
              </AppButton>
              <ExportData
                data={getExportData()}
                columns={exportStoreColumns}
                filename={`stores-${new Date().toISOString().split("T")[0]}`}
                title={t("store-export-title", "Export Store Data")}
                buttonVariant="outlined"
                buttonText={
                  selectedIds.length > 0
                    ? t(
                        "store-export-selected",
                        "Export Selected ({{count}})",
                        { count: selectedIds.length }
                      )
                    : t("export", "Export")
                }
                formats={["csv", "xlsx", "pdf", "json"]}
                onExportStart={() => console.log("Export started")}
                onExportComplete={(format) =>
                  console.log(`Export completed: ${format}`)
                }
                onExportError={(error) => console.error("Export error:", error)}
              />
            </Box>
          </Box>
        </Box>

        <Box
          display="flex"
          justifyContent="right"
          alignItems="center"
          mt={2}
          gap={2}
          flexDirection={matchMD ? "column" : "row"}
        >
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            flexDirection={matchMD ? "column" : "row"}
          >
            <TextField
              size="small"
              placeholder={t("search", "Search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Box>
        </Box>

        {/* Table Section */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    color="primary"
                    checked={
                      selectedIds.length === sortedList.length &&
                      sortedList.length > 0
                    }
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < sortedList.length
                    }
                    onChange={handleSelectAll}
                  />
                </TableCell>

                {/* Conditional columns */}
                {matchMD
                  ? [
                      {
                        id: "name",
                        label: t("store-column-names", "Names")
                      }
                    ].map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={() =>
                            handleSort(column.id as keyof CompanyStore)
                          }
                          sx={{ fontSize: 13, fontWeight: 600 }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))
                  : [
                      {
                        id: "id",
                        label: t("store-column-id", "StoreId")
                      },
                      {
                        id: "name",
                        label: t("store-column-name", "Name")
                      },
                      {
                        id: "status",
                        label: t("store-column-status", "Status")
                      }
                    ].map((column) => (
                      <TableCell
                        key={column.id}
                        sortDirection={orderBy === column.id ? order : false}
                      >
                        <TableSortLabel
                          active={orderBy === column.id}
                          direction={orderBy === column.id ? order : "asc"}
                          onClick={() =>
                            handleSort(column.id as keyof CompanyStore)
                          }
                          sx={{ fontSize: 13, fontWeight: 600 }}
                        >
                          {column.label}
                        </TableSortLabel>
                      </TableCell>
                    ))}

                <TableCell
                  sx={{ fontSize: 13, fontWeight: 600 }}
                  align="center"
                >
                  {t("store-column-actions", "Actions")}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {sortedList.map((store) => (
                <TableRow
                  key={store.id}
                  hover
                  onClick={() => goToStoreDetail(store.id)}
                  sx={{
                    "&:hover": {
                      backgroundColor: "action.hover",
                      cursor: "pointer"
                    }
                  }}
                >
                  <TableCell
                    padding="checkbox"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Checkbox
                      color="primary"
                      checked={selectedIds.includes(store.id)}
                      onChange={() => handleSelectOne(store.id)}
                    />
                  </TableCell>

                  {!matchMD && (
                    <TableCell>
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" sx={{ fontSize: 13 }}>
                          {store.id}
                        </Typography>
                      </Stack>
                    </TableCell>
                  )}

                  <TableCell>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ fontSize: 13 }}>
                        {store.name}
                      </Typography>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ fontSize: 13 }}>
                    {store.status ?? "N/A"}
                  </TableCell>

                  <TableCell align="center" sx={{ fontSize: 13 }}>
                    <IconButton
                      aria-label={t("store-view-aria-label", "View Store")}
                      data-testid={`view-store-${store.id}`}
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        goToStoreDetail(store.id);
                      }}
                    >
                      <VisibilityOutlinedIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Add Store Modal */}
        <AddStoreModal
          open={openAddModal}
          onClose={() => setOpenAddModal(false)}
        />
      </Box>

      {/* Pagination */}
      <Box display="flex" justifyContent="center" mt={3}>
        <Pagination
          variant="outlined"
          count={Math.ceil(sortedList.length / 10)}
          shape="rounded"
        />
      </Box>
    </Box>
  );
};

export default StoreList;
